import type { NamedValueCacheInterface } from '../../Platform';
import { ApiFeatures, FeatureEnrollmentPopulateError } from './types';

export class FeatureEnrollment {
    #features: Map<string, boolean> = new Map();
    #platform: NamedValueCacheInterface;

    constructor(p: NamedValueCacheInterface) {
        this.#platform = p;
    }

    /**
     *
     * @throws {@link FeatureEnrollmentPopulateError} if some or all features failed to populate.
     */
    async initialize(apiResult: ApiFeatures): Promise<void> {
        const promises = apiResult.map((f) => this.#setEnrollment(f.name, f.is_player_enrolled));
        const results = await Promise.allSettled(promises);
        const failures = results
            .map((r, i) => {
                return {
                    name: apiResult[i].name,
                    errorReason: r.status === 'rejected' ? String(r.reason) : '__none__',
                };
            })
            .filter((r) => r.errorReason !== '__none__');
        if (failures.length > 0) {
            throw new FeatureEnrollmentPopulateError(
                'Some feature enrollments failed to populate.',
                failures
            );
        }
    }

    async #setEnrollment(name: string, enrolled: boolean): Promise<void> {
        this.#features.set(name, enrolled);
        const keyName = `feature__${name}`;
        if (enrolled) {
            await this.#platform.setNamedValue(keyName, 'true');
        } else {
            await this.#platform.deleteNamedValue(keyName);
        }
    }

    /**
     * Returns true if this particular signage instance is enrolled in a feature
     */
    isEnrolledIn(name: string): boolean {
        return this.#features.get(name) ?? false;
    }
}
